/**
 * Aggregierter Stand eines Vorgangs, der im Hintergrund weiterläuft — die gemeinsame Datenform der
 * Badge in der App-Shell.
 *
 * Bewusst eine Sicht für Upload UND Löschen: beide halten ihre Warteschlange in einem
 * `providedIn: 'root'`-Service, laufen beim Navigieren weiter und waren außerhalb der Dateiansicht
 * trotzdem unsichtbar. Die Badge soll nicht wissen müssen, welcher Vorgang gerade läuft oder wie er
 * intern zerlegt ist.
 *
 * Für das Löschen wird diese Sicht mittelfristig aus dem Server-Batch gefüllt statt aus der
 * Client-Warteschlange; die Anzeige bleibt davon unberührt.
 */
export interface BackgroundProgress {
  total: number;
  done: number;
  failed: number;
  /** Noch nicht abgeschlossen. */
  active: number;
  /** Namen der gerade laufenden Einheiten. */
  currentNames: string[];
  failedNames: string[];
  /**
   * Projekt, zu dem die gezeigte Arbeit gehört — damit die Badge in DESSEN Dateiansicht springen
   * kann und nicht in die des gerade ausgewählten Projekts.
   *
   * Das ist nicht dasselbe: Upload und Löschen laufen beim Projektwechsel weiter, die Badge steht
   * dann über Arbeit aus Projekt A, während Projekt B ausgewählt ist.
   *
   * In den `progressByProject$`-Sichten ist das Feld immer gesetzt — sie sind ja nach genau diesem
   * Wert gruppiert. In der zusammengefassten `progress$`-Sicht ist es nur gesetzt, solange alles
   * zu EINEM Projekt gehört; bei mehreren bleibt es undefiniert, weil es keine ehrliche Antwort
   * gibt. Die Badge arbeitet deshalb mit den gruppierten Sichten.
   */
  projectId?: number;
}

/**
 * Fasst mehrere Projekt-Stände zu einem zusammen — für alles, was tabweit gilt (Reiter-Zähler,
 * „läuft überhaupt noch etwas?"-Abfragen), und um die Zählerlogik nicht zweimal zu schreiben.
 *
 * `projectId` überlebt die Zusammenfassung nur, wenn alle Teile dasselbe Projekt meinen.
 */
export function mergeProgress(parts: BackgroundProgress[]): BackgroundProgress {
  const projectIds = new Set(parts.map(p => p.projectId).filter(id => id != null));
  return {
    total: parts.reduce((sum, p) => sum + p.total, 0),
    done: parts.reduce((sum, p) => sum + p.done, 0),
    failed: parts.reduce((sum, p) => sum + p.failed, 0),
    active: parts.reduce((sum, p) => sum + p.active, 0),
    currentNames: parts.flatMap(p => p.currentNames),
    failedNames: parts.flatMap(p => p.failedNames),
    projectId: projectIds.size === 1 ? [...projectIds][0] : undefined,
  };
}

/** Leerer Stand — für Startwerte und Tests. */
export const EMPTY_BACKGROUND_PROGRESS: BackgroundProgress = {
  total: 0,
  done: 0,
  failed: 0,
  active: 0,
  currentNames: [],
  failedNames: [],
  projectId: undefined,
};
